import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AutosizeInput from 'dpl/common/components/AutosizeInput';
import DatePicker, { DATEPICKER_ID } from 'dpl/common/components/DatePicker';
import Label from 'dpl/form/components/Label';
import Select from 'dpl/form/components/Select';
import Textarea from 'dpl/form/components/Textarea';

import { AssignBuyerToLitterInputV1 } from './AssignBuyerToLitterInputV1';

export default function PaymentRequestAmountQuestion({
  amountInDollars,
  autoFocus,
  availableCategories,
  buyerProfile,
  category,
  dueAt,
  isBuyerRequest,
  onChange,
  onLitterChange,
  shouldShowEditButton
}) {
  const inputInstanceRef = useRef();
  const [isInputLocked, setIsInputLocked] = useState(shouldShowEditButton);

  const datePickerStartDate = new Date();
  const amountInput = inputInstanceRef.current?.inputRef?.current;

  datePickerStartDate.setDate(datePickerStartDate.getDate() - 1);

  useEffect(() => {
    if (!isInputLocked) amountInput?.focus();
  }, [isInputLocked]);

  return (
    <div className="BreederAppPaymentRequestAmountQuestion">
      <div className="mb2-lg mb4 ph8 no-outline tc nowrap overflow-hidden">
        <span className="BreederAppPaymentRequestAmountQuestion__dollarSign absolute v-top f7">
          $
        </span>
        <AutosizeInput
          ref={inputInstanceRef}
          autoFocus={autoFocus}
          className={classNames(
            'BreederAppPaymentRequestAmountQuestion__amountInput bw0 relative dib fw-light bg-transparent',
            { editing: shouldShowEditButton && !isInputLocked }
          )}
          maxWidth="340px"
          name="amount_in_dollars"
          onChange={onChange}
          onKeyDown={e => {
            if (e.key === 'Enter') setIsInputLocked(true);
          }}
          placeholder="0"
          type="number"
          value={amountInDollars || ''}
          disabled={isInputLocked}
        />
        {shouldShowEditButton && (
          <button
            className="BreederAppPaymentRequestAmountQuestion__editButton center db underline z-3 mt2"
            type="button"
            onClick={() => {
              setIsInputLocked(!isInputLocked);
            }}
          >
            {isInputLocked ? 'Edit amount' : 'Done'}
          </button>
        )}
      </div>
      <div className="mb5">
        <Select
          label="Category"
          name="category"
          options={availableCategories}
        />
      </div>

      {!isBuyerRequest && (
        <div className="mb5">
          <Label
            text="Due date (optional)"
            htmlFor={`${DATEPICKER_ID}-due_at`}
          />
          <DatePicker
            date={dueAt}
            displayCalendarIcon
            inputId="due_at"
            name="due_at"
            onChange={date => {
              onChange({ target: { name: 'due_at', value: date?.toJSON() } });
            }}
            placeholder="Select a date"
            startDate={datePickerStartDate}
          />
        </div>
      )}

      {!isBuyerRequest && buyerProfile && (
        <AssignBuyerToLitterInputV1
          buyerProfile={buyerProfile}
          className="tl mb5"
          onChange={onLitterChange}
        />
      )}

      {(isBuyerRequest || category) && (
        <div className="mb5">
          <Textarea
            noResize
            label="Personal message (optional)"
            name="personal_message"
          />
        </div>
      )}
    </div>
  );
}

PaymentRequestAmountQuestion.propTypes = {
  amountInDollars: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autoFocus: PropTypes.bool,
  availableCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  buyerProfile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    full_name: PropTypes.string.isRequired
  }),
  category: PropTypes.string,
  dueAt: PropTypes.string,
  isBuyerRequest: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onLitterChange: PropTypes.func.isRequired,
  shouldShowEditButton: PropTypes.bool
};

PaymentRequestAmountQuestion.defaultProps = {
  amountInDollars: null,
  autoFocus: false,
  buyerProfile: null,
  category: null,
  dueAt: null,
  isBuyerRequest: false,
  shouldShowEditButton: false
};
